import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { fetchContent, verifyToken, fetchGallery } from './api';
import { getTypo, FONT_LIBRARY, preloadAllFonts } from './typographyConfig';
import AdminLogin from './admin/AdminLogin';
import AdminPanel from './admin/AdminPanel';
import './App.css';

/* ═══════════════════════════════════════════════════════════════
   Circle Studios — Full Homepage + Admin CMS
   ═══════════════════════════════════════════════════════════════ */

const LOGO_SRC = '/logo-horizontal.png';
const LOGO_STACKED_SRC = '/logo-stacked.png';

const DEFAULT_HERO_VIDEO = '';
const DEFAULT_HERO_POSTER = 'https://customer-assets.emergentagent.com/job_e157f8b6-f50b-488c-8623-9c43c798a131/artifacts/al0peqk6_ChatGPT%20Image%20Apr%2013%2C%202026%2C%2010_48_51%20PM.png';

const DEFAULT_CONTENT = {
  hero: {
    video: null,
    poster: { type: 'url', value: DEFAULT_HERO_POSTER },
    headlineLine1: 'Where',
    headlineLine2: 'Experiences',
    headlineLine3: 'Become Influence',
    subheadline: 'Circle Studios is an omni-channel experience studio connecting live engagement, strategy, content distribution, and audience growth across sports, entertainment, innovation, enterprise, and culture.',
    ctaPrimaryText: 'Our Work',
    ctaPrimaryLink: '#gallery',
    ctaSecondaryText: 'Connect With Us',
    ctaSecondaryLink: '#contact',
  },
  trustedPartners: {
    logos: [
      { src: '/logos/hexagon.png', alt: 'Hexagon' },
      { src: '/logos/hitachi.png', alt: 'Hitachi' },
      { src: '/logos/arm.png', alt: 'Arm' },
      { src: '/logos/live_nation.png', alt: 'Live Nation' },
      { src: '/logos/sphere.png', alt: 'Sphere' },
      { src: '/logos/swarovski.png', alt: 'Swarovski' },
      { src: '/logos/devolver.png', alt: 'Devolver Digital' },
      { src: '/logos/iheartmedia.png', alt: 'iHeartMedia' },
      { src: '/logos/chevrolet.png', alt: 'Chevrolet' },
      { src: '/logos/cocacola.png', alt: 'Coca-Cola' },
      { src: '/logos/hennessy.png', alt: 'Hennessy' },
      { src: '/logos/adobe.png', alt: 'Adobe' },
      { src: '/logos/benjerrys.png', alt: "Ben & Jerry's" },
      { src: '/logos/cape_analytics.png', alt: 'Cape Analytics' },
      { src: '/logos/sony.png', alt: 'Sony' },
      { src: '/logos/goodyear.png', alt: 'Goodyear' },
      { src: '/logos/ritchie_bros.png', alt: 'Ritchie Bros.' },
      { src: '/logos/paramount.png', alt: 'Paramount' },
      { src: '/logos/ar_emblem.png', alt: 'AR' },
      { src: '/logos/sanmina.png', alt: 'Sanmina' },
      { src: '/logos/adidas.png', alt: 'Adidas' },
      { src: '/logos/empire.png', alt: 'Empire' },
      { src: '/logos/catalina.png', alt: 'Catalina' },
      { src: '/logos/piccadilly.png', alt: 'Piccadilly' },
      { src: '/logos/axeno.png', alt: 'Axeno' },
      { src: '/logos/att.png', alt: 'AT&T' },
      { src: '/logos/espolon.png', alt: 'Espolon Tequila' },
      { src: '/logos/bloomreach.png', alt: 'Bloomreach' },
      { src: '/logos/reebok.png', alt: 'Reebok' },
      { src: '/logos/nasa.png', alt: 'NASA' },
      { src: '/logos/warner_bros.png', alt: 'Warner Bros.' },
      { src: '/logos/amazon.png', alt: 'Amazon' },
      { src: '/logos/bcg.png', alt: 'BCG' },
      { src: '/logos/vtex.png', alt: 'VTEX' },
      { src: '/logos/donjulio.png', alt: 'Don Julio' },
      { src: '/logos/ford.png', alt: 'Ford' },
      { src: '/logos/insomniac.png', alt: 'Insomniac' },
      { src: '/logos/raiders.png', alt: 'Raiders' },
    ],
  },
  about: {
    title: 'A new model for experience and media.',
    text: 'Circle Studios operates at the intersection of strategy, design, production, and media\u2014creating experiences that extend beyond the moment into scalable platforms.',
    philosophy: [
      'We build with intention.',
      'We execute with precision.',
      'We measure what matters.',
    ],
  },
  capabilities: {
    title: 'Capabilities',
    subtitle: 'End-to-End Execution Across the Experience Lifecycle',
    video: null,
    items: [
      { title: 'Strategy & Advisory', text: 'Defining the vision, positioning, and roadmap that drives meaningful outcomes across every channel.', bullets: ['Positioning', 'Audience mapping', 'Platform design', 'Go-to-market alignment'] },
      { title: 'Creative & Design', text: 'Crafting bold visual identities and immersive experiences that command attention and inspire action.', bullets: ['Environmental design', 'Brand storytelling', 'Spatial planning', 'Visual systems'] },
      { title: 'Production & Build', text: 'End-to-end physical and digital production \u2014 from concept to flawless execution at any scale.', bullets: ['Fabrication', 'Technical integration', 'Logistics', 'On-site execution'] },
      { title: 'Content & Media', text: 'Producing and distributing stories that resonate \u2014 across broadcast, social, and streaming platforms.', bullets: ['Broadcast', 'Digital content', 'Storytelling', 'Distribution strategies'] },
      { title: 'Technology Integration', text: 'Connecting platforms, data, and tools into seamless ecosystems that power next-generation experiences.', bullets: ['Interactive systems', 'Data capture', 'AR/VR', 'Emerging technologies'] },
      { title: 'Data & Measurement', text: 'Capturing insights that matter \u2014 tracking performance, audience behavior, and ROI in real time.', bullets: ['Analytics', 'Audience insights', 'Performance optimization'] },
    ],
    stats: [
      { num: 1000, suffix: '+', label: 'Live Experiences Produced' },
      { num: 50, suffix: '+', label: 'Countries Activated Globally' },
      { num: 100, suffix: 'M+', label: 'Audience Reach Across Events + Media' },
      { num: 98, suffix: '%', label: 'Client Retention Rate' },
    ],
  },
  sustainability: {
    title: 'Sustainability First',
    intro: 'Circle Studios is redefining how live experiences are created by embedding sustainability directly into design, production, and deployment.',
    retreadPlatformVideo: null,
    stixPlatformVideo: null,
    impactItems: [
      { icon: '01', label: 'Material Use', stat: 'Reduced', desc: 'Multi-cycle modular systems eliminate single-use material consumption.' },
      { icon: '02', label: 'Waste', stat: 'Eliminated', desc: 'Reusable infrastructure diverts thousands of pounds from landfill.' },
      { icon: '03', label: 'Transportation', stat: 'Optimized', desc: 'Compact stackable components cut freight volume and emissions.' },
      { icon: '04', label: 'On-Site Ops', stat: 'Streamlined', desc: 'Rapid install/strike systems cut labor hours and energy use.' },
      { icon: '05', label: 'Footprint', stat: 'Minimized', desc: 'End-to-end lifecycle design reduces environmental impact.' },
    ],
    platforms: [
      { id: 'stix', name: 'STIX Modular Exhibit System', tagline: 'Reconfigurable exhibit architecture that eliminates waste.', bullets: ['Adapts to any venue or format', 'Eliminates single-use builds', 'Ships flat, assembles fast', 'Premium finish, sustainable core'], accent: 'blue' },
      { id: 'retread', name: 'ReTread Surfaces\u2122', tagline: 'Modular flooring engineered for infinite reuse.', bullets: ['50+ lifecycle deployments per unit', 'Zero-landfill recoverability', 'Rapid install in under 4 hours', 'Custom finish & branding options'], accent: 'violet' },
    ],
  },
  proprietary: {
    sectionLabel: 'Explore',
    sectionTitle: 'Proprietary Engagements',
    cards: [
      { name: 'SEICON', href: 'https://sei-con.org/', color: '#E8443A', accent: '#FF6B5E', visible: true },
      { name: 'FANPRIX', href: 'https://thefanprix.com/', color: '#E8A817', accent: '#FFD040', visible: true },
      { name: 'OZFEST', href: 'https://premium-ozfest.emergent.host', color: '#10B9A0', accent: '#34D3B8', visible: true },
    ],
  },
  contact: {
    eyebrow: 'Start a Conversation',
    title: 'Ready to build\nsomething\ninfluential?',
    subtitle: "We partner with visionaries who refuse to settle. Tell us about your ambition — we'll architect the path.",
    ctaText: 'Begin Inquiry',
    ctaLink: 'mailto:info@circlestudios.com',
  },
  eventLogos: {
    logos: [
      {src: '/event-logos/pitchfork.png', alt: 'Pitchfork', order: 1},
      {src: '/event-logos/gdc.png', alt: 'GDC', order: 2},
      {src: '/event-logos/heritage_fire.png', alt: 'Heritage Fire', order: 3},
      {src: '/event-logos/f1_miami_gp.png', alt: 'F1 Miami Grand Prix', order: 4},
      {src: '/event-logos/rock_n_rye.png', alt: 'Rock N Rye', order: 5},
      {src: '/event-logos/govball.png', alt: 'Govball', order: 6},
      {src: '/event-logos/d23.png', alt: 'D23', order: 7},
      {src: '/event-logos/cop28_uae.png', alt: 'COP28 UAE', order: 8},
      {src: '/event-logos/adit_live.png', alt: 'Adit Live', order: 9},
      {src: '/event-logos/milken_institute.png', alt: 'Milken Institute', order: 10},
      {src: '/event-logos/ted_2024.png', alt: 'TED 2024', order: 11},
      {src: '/event-logos/couture.png', alt: 'Couture', order: 12},
      {src: '/event-logos/ces.png', alt: 'CES', order: 13},
      {src: '/event-logos/breakthrough_energy.png', alt: 'Breakthrough Energy', order: 14},
      {src: '/event-logos/minexpo.png', alt: 'MineXpo', order: 15},
      {src: '/event-logos/innings_festival.png', alt: 'Innings Festival', order: 16},
      {src: '/event-logos/chefs_for_farmers.png', alt: 'Chefs for Farmers', order: 17},
      {src: '/event-logos/shaky_knees.png', alt: 'Shaky Knees Music Fest', order: 18},
      {src: '/event-logos/whiskies_of_the_world.png', alt: 'Whiskies of the World', order: 19},
      {src: '/event-logos/broccoli_city.png', alt: 'Broccoli City Festival', order: 20},
      {src: '/event-logos/the_whisky_x.png', alt: 'The Whisky X', order: 21},
      {src: '/event-logos/google_cloud_next.png', alt: 'Google Cloud Next 24', order: 22},
      {src: '/event-logos/conexpo_conagg.png', alt: 'ConExpo Con/Agg', order: 23},
      {src: '/event-logos/sobewff.png', alt: 'SOBEWFF', order: 24},
      {src: '/event-logos/nab_show.png', alt: 'NAB Show', order: 25},
      {src: '/event-logos/nrf.png', alt: 'NRF', order: 26},
      {src: '/event-logos/inter_miami.png', alt: 'Inter Miami CF', order: 27},
      {src: '/event-logos/audi_field.png', alt: 'Audi Field', order: 28},
      {src: '/event-logos/worlds_50_best.png', alt: 'Worlds 50 Best Restaurants', order: 29},
      {src: '/event-logos/aws_reinvent.png', alt: 'AWS re:Invent', order: 30},
      {src: '/event-logos/f1.png', alt: 'Formula 1', order: 31},
      {src: '/event-logos/lollapalooza.png', alt: 'Lollapalooza', order: 32},
      {src: '/event-logos/coachella.png', alt: 'Coachella', order: 33},
      {src: '/event-logos/vidsummit.png', alt: 'VidSummit', order: 34},
      {src: '/event-logos/expo_world_market.png', alt: 'The Expo at World Market Center', order: 35},
      {src: '/event-logos/shoptalk.png', alt: 'Shoptalk', order: 36},
      {src: '/event-logos/black_hat.png', alt: 'Black Hat', order: 37},
    ],
  },
  footer: {
    navLinks: [
      { label: 'About', href: '#about' },
      { label: 'Capabilities', href: '#capabilities' },
      { label: 'Our Work', href: '#gallery' },
      { label: 'Sustainability', href: '#sustainability' },
      { label: 'Contact', href: '#contact' },
    ],
    socialLabel: 'Social',
    tagline: 'Circle Studios \u2014 Where Experiences Become Influence',
  },
  typography: {},
};

// Helper: resolve media value
function resolveMedia(media, fallback) {
  if (!media) return fallback || '';
  if (typeof media === 'string') return media;
  return media.value || fallback || '';
}

// Helper: deep-merge content with defaults (1-level deep for section objects)
function mergeContent(api, defaults) {
  if (!api) return defaults;
  const merged = {};
  const allKeys = new Set([...Object.keys(defaults), ...Object.keys(api)]);
  for (const key of allKeys) {
    const def = defaults[key];
    const val = api[key];
    if (val === undefined || val === null) {
      merged[key] = def;
    } else if (def && typeof def === 'object' && !Array.isArray(def) && typeof val === 'object' && !Array.isArray(val)) {
      merged[key] = { ...def, ...val };
    } else {
      merged[key] = val;
    }
  }
  return merged;
}

// ─── Hooks ──────────────────────────────────────────────────

function useInView(options = {}) {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15, ...options }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
}

function useStagger(isInView, count, delayMs = 100) {
  const [visibleItems, setVisibleItems] = useState([]);

  useEffect(() => {
    if (!isInView) return;
    const timers = [];
    for (let i = 0; i < count; i++) {
      timers.push(
        setTimeout(() => {
          setVisibleItems((prev) => [...prev, i]);
        }, i * delayMs)
      );
    }
    return () => timers.forEach(clearTimeout);
  }, [isInView, count, delayMs]);

  return visibleItems;
}

// ─── Scroll Progress Bar ────────────────────────────────────
function ScrollProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let frame = null;
    let pending = 0;
    const flush = () => {
      frame = null;
      setProgress(pending);
    };
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      pending = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      if (frame == null) frame = requestAnimationFrame(flush);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="scroll-progress-bar" data-testid="scroll-progress">
      <div className="scroll-progress-fill" style={{ width: `${progress}%` }} />
    </div>
  );
}

// ─── #2: Scroll Reveal Text ─────────────────────────────────
function ScrollRevealText({ text, tag: Tag = 'h2', className = '', style = {} }) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setRevealed(true); observer.unobserve(el); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const words = useMemo(() => text.split(' '), [text]);

  return (
    <Tag ref={ref} className={`scroll-reveal-text ${className} ${revealed ? 'sr-revealed' : ''}`} style={style}>
      {words.map((word, i) => (
        <span key={i} className="sr-word" style={{ transitionDelay: `${i * 0.07}s` }}>
          {word}
        </span>
      ))}
    </Tag>
  );
}

// ─── #5: Animated Section Divider ───────────────────────────
function AnimatedDivider() {
  const [ref, inView] = useInView({ threshold: 0.5 });

  return (
    <div className={`anim-divider ${inView ? 'anim-divider-visible' : ''}`} ref={ref} aria-hidden="true" data-testid="animated-divider">
      <div className="anim-divider-line" />
      <div className="anim-divider-dot" />
      <div className="anim-divider-line" />
    </div>
  );
}

// ─── #6: Floating CTA ──────────────────────────────────────
function FloatingCTA() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let frame = null;
    let pendingVisible = false;
    const flush = () => {
      frame = null;
      setVisible((curr) => (curr === pendingVisible ? curr : pendingVisible));
    };
    const onScroll = () => {
      const scrollY = window.scrollY;
      const contactEl = document.getElementById('contact');
      const contactTop = contactEl ? contactEl.getBoundingClientRect().top : 9999;
      pendingVisible = scrollY > 400 && contactTop > 200;
      if (frame == null) frame = requestAnimationFrame(flush);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, []);

  const handleClick = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <button
      className={`floating-cta ${visible ? 'floating-cta-visible' : ''}`}
      onClick={handleClick}
      aria-label="Get in touch"
      data-testid="floating-cta"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
      <span>Let's Talk</span>
    </button>
  );
}

// ─── Hero Magnetic Particles (Canvas-based) ─────────────────
function HeroMagneticParticles() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000, active: false });
  const particlesRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const PARTICLE_COUNT = 25;
    const MAGNETIC_RADIUS = 250;
    const MAGNETIC_STRENGTH = 0.035;
    const RETURN_SPEED = 0.012;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Initialize particles
    if (!particlesRef.current) {
      particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () => {
        const homeX = Math.random() * canvas.width;
        const homeY = Math.random() * canvas.height;
        return {
          x: homeX, y: homeY,
          homeX, homeY,
          vx: 0, vy: 0,
          size: 2 + Math.random() * 3.5,
          opacity: 0.18 + Math.random() * 0.35,
          driftSpeed: 0.15 + Math.random() * 0.3,
          driftAngle: Math.random() * Math.PI * 2,
          driftRadius: 20 + Math.random() * 40,
          phase: Math.random() * Math.PI * 2,
        };
      });
    }

    const particles = particlesRef.current;

    // Reposition homes on resize
    const reposition = () => {
      particles.forEach(p => {
        p.homeX = Math.random() * canvas.width;
        p.homeY = Math.random() * canvas.height;
        p.x = p.homeX;
        p.y = p.homeY;
      });
    };
    if (canvas.width > 0) reposition();

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      // Check if mouse is within the hero canvas bounds
      if (x >= 0 && x <= rect.width && y >= 0 && y <= rect.height) {
        mouseRef.current.x = x;
        mouseRef.current.y = y;
        mouseRef.current.active = true;
      } else {
        mouseRef.current.active = false;
      }
    };
    const onMouseLeave = () => {
      mouseRef.current.active = false;
    };

    // Listen on document to capture mouse even when over hero-content children
    document.addEventListener('mousemove', onMouseMove);
    const heroSection = canvas.closest('.hero-section');
    if (heroSection) {
      heroSection.addEventListener('mouseleave', onMouseLeave);
    }

    let time = 0;
    const animate = () => {
      time += 0.008;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(p => {
        // Ambient drift
        const driftX = Math.cos(time * p.driftSpeed + p.phase) * p.driftRadius;
        const driftY = Math.sin(time * p.driftSpeed * 0.7 + p.phase + 1) * p.driftRadius * 0.6;
        const targetX = p.homeX + driftX;
        const targetY = p.homeY + driftY;

        let proximity = 0; // 0 = far, 1 = right on cursor
        if (mouseRef.current.active) {
          const dx = mouseRef.current.x - p.x;
          const dy = mouseRef.current.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < MAGNETIC_RADIUS && dist > 1) {
            proximity = 1 - dist / MAGNETIC_RADIUS;
            const force = proximity * MAGNETIC_STRENGTH;
            const easedForce = force * force * 4;
            p.vx += dx * easedForce;
            p.vy += dy * easedForce;
          }
        }

        // Return to ambient position
        p.vx += (targetX - p.x) * RETURN_SPEED;
        p.vy += (targetY - p.y) * RETURN_SPEED;

        // Damping
        p.vx *= 0.91;
        p.vy *= 0.91;

        p.x += p.vx;
        p.y += p.vy;

        // Draw — grow and brighten when magnetized
        const magnetScale = 1 + proximity * 1.2;
        const magnetOpacity = p.opacity + proximity * 0.4;
        const drawSize = p.size * magnetScale;

        ctx.beginPath();
        ctx.arc(p.x, p.y, drawSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(220, 220, 230, ${Math.min(magnetOpacity, 0.9)})`;
        ctx.fill();

        // Soft glow halo when close to cursor
        if (proximity > 0.2) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, drawSize * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(200, 200, 240, ${proximity * 0.08})`;
          ctx.fill();
        }
      });

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      document.removeEventListener('mousemove', onMouseMove);
      if (heroSection) {
        heroSection.removeEventListener('mouseleave', onMouseLeave);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="hero-magnetic-particles"
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        pointerEvents: 'none',
      }}
    />
  );
}

// ─── Floating Particles (CSS, used in non-hero sections) ────
function FloatingParticles({ count = 20, className = '' }) {
  const particles = useRef(
    Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: 2 + Math.random() * 4,
      duration: 8 + Math.random() * 12,
      delay: Math.random() * 5,
      opacity: 0.15 + Math.random() * 0.25,
    }))
  ).current;

  return (
    <div className={`floating-particles ${className}`} aria-hidden="true">
      {particles.map((p) => (
        <span
          key={p.id}
          className="particle"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            opacity: p.opacity,
          }}
        />
      ))}
    </div>
  );
}

// ─── Navigation ─────────────────────────────────────────────
function SiteNav({ typo }) {
  const t = typo || {};
  const bts = (k) => {
    if (!t[k]) return {};
    const s = {};
    if (t[k].fontFamily) s.fontFamily = t[k].fontFamily;
    if (t[k].fontSize) s.fontSize = t[k].fontSize;
    if (t[k].fontWeight) s.fontWeight = t[k].fontWeight;
    if (t[k].textTransform) s.textTransform = t[k].textTransform;
    if (t[k].letterSpacing) s.letterSpacing = t[k].letterSpacing;
    if (t[k].lineHeight) s.lineHeight = t[k].lineHeight;
    return s;
  };
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [navLogoVisible, setNavLogoVisible] = useState(false);

  // Scroll handler — nav background, logo visibility, and scrollspy.
  // Uses requestAnimationFrame to coalesce multiple scroll fires per frame
  // into a single setState batch — prevents 60+ re-renders/sec on scroll.
  useEffect(() => {
    // Sections in DOM order (top→bottom on page) — critical for correct scrollspy
    const sectionIds = ['about', 'capabilities', 'gallery', 'sustainability', 'contact'];
    let frame = null;
    const compute = () => {
      frame = null;
      const sY = window.scrollY;
      // Use functional setState so we only trigger a re-render when value changes.
      setScrolled((curr) => (sY > 60) === curr ? curr : sY > 60);
      const heroLogo = document.querySelector('[data-testid="hero-logo"]');
      let nextLogoVisible = true;
      if (heroLogo) {
        const rect = heroLogo.getBoundingClientRect();
        nextLogoVisible = rect.bottom < 0;
      }
      setNavLogoVisible((curr) => (curr === nextLogoVisible ? curr : nextLogoVisible));
      // Scrollspy: last section whose top crossed the 40% viewport line wins
      const offset = window.innerHeight * 0.4;
      let current = '';
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= offset) current = id;
      }
      setActiveSection((curr) => (curr === current ? curr : current));
    };
    const onScroll = () => {
      if (frame == null) frame = requestAnimationFrame(compute);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    compute();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, []);

  const navLinks = [
    { label: 'About', href: '#about' },
    { label: 'Capabilities', href: '#capabilities' },
    { label: 'Our Work', href: '#gallery' },
    { label: 'Sustainability', href: '#sustainability' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <nav className={`site-nav ${scrolled ? 'nav-scrolled' : ''}`} data-testid="site-nav">
      <div className="nav-inner">
        <a href="#" className={`nav-logo ${navLogoVisible ? 'nav-logo-visible' : 'nav-logo-hidden'}`} data-testid="nav-logo">
          <img src={LOGO_SRC} alt="Circle Studios" />
        </a>
        <div className={`nav-links ${menuOpen ? 'nav-links-open' : ''}`} data-testid="nav-links">
          {navLinks.map((link, i) => (
            <a
              key={i}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                setMenuOpen(false);
                document.getElementById(link.href.slice(1))?.scrollIntoView({ behavior: 'smooth' });
              }}
              className={activeSection === link.href.slice(1) ? 'nav-active' : ''}
              data-testid={`nav-link-${i}`}
              style={bts('navLinks')}
            >
              {link.label}
            </a>
          ))}
        </div>
        <button
          className="nav-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          data-testid="nav-hamburger"
        >
          <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
          <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
          <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
        </button>
      </div>
    </nav>
  );
}

// ─── Section 1: Hero ────────────────────────────────────────
function HeroSection({ content, typo }) {
  const t = typo || {};
  const bts = (k) => {
    if (!t[k]) return {};
    const s = {};
    if (t[k].fontFamily) s.fontFamily = t[k].fontFamily;
    if (t[k].fontSize) s.fontSize = t[k].fontSize;
    if (t[k].fontWeight) s.fontWeight = t[k].fontWeight;
    if (t[k].textTransform) s.textTransform = t[k].textTransform;
    if (t[k].letterSpacing) s.letterSpacing = t[k].letterSpacing;
    if (t[k].lineHeight) s.lineHeight = t[k].lineHeight;
    return s;
  };
  const [loaded, setLoaded] = useState(false);
  const [introPhase, setIntroPhase] = useState('logo'); // 'logo' | 'transition' | 'done'
  const videoRef = useRef(null);
  const hero = content || DEFAULT_CONTENT.hero;

  useEffect(() => {
    // Phase 1: Logo holds for ~0.8s
    const t1 = setTimeout(() => setIntroPhase('transition'), 800);
    // Phase 2: Logo shrinks + content reveals simultaneously over ~1s
    const t2 = setTimeout(() => {
      setIntroPhase('done');
      setLoaded(true);
    }, 1900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const videoUrl = resolveMedia(hero.video, '');
  const posterUrl = resolveMedia(hero.poster, DEFAULT_HERO_POSTER);
  const isVideoSrc = videoUrl && /\.(mp4|webm|ogg|mov)($|\?)/i.test(videoUrl);

  const prevVideoUrl = useRef(videoUrl);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideoSrc) return;
    if (prevVideoUrl.current !== videoUrl) {
      prevVideoUrl.current = videoUrl;
      video.load();
    }
    const tryPlay = () => {
      if (video.paused && video.readyState >= 2) {
        video.play().catch(() => {});
      }
    };
    video.addEventListener('canplay', tryPlay);
    video.addEventListener('loadeddata', tryPlay);
    const retryTimer = setTimeout(() => {
      if (video.paused) video.play().catch(() => {});
    }, 2000);
    return () => {
      video.removeEventListener('canplay', tryPlay);
      video.removeEventListener('loadeddata', tryPlay);
      clearTimeout(retryTimer);
    };
  }, [videoUrl, isVideoSrc]);

  return (
    <section className="hero-section" data-testid="hero-section">
      <div className="hero-bg-video" data-testid="hero-video-placeholder">
        {isVideoSrc ? (
          <video
            ref={videoRef}
            src={videoUrl}
            autoPlay
            muted
            loop
            playsInline
            poster={posterUrl}
            className="hero-video-el"
          />
        ) : (
          <img src={posterUrl} alt="" className="hero-bg-img" />
        )}
      </div>
      <div className="hero-bg-overlay" />
      <div className="hero-aurora" aria-hidden="true" />

      {/* Cinematic Intro Overlay */}
      <div className={`hero-intro-overlay ${introPhase === 'logo' ? 'intro-phase-logo' : ''} ${introPhase === 'transition' ? 'intro-phase-transition' : ''} ${introPhase === 'done' ? 'intro-phase-done' : ''}`} aria-hidden={introPhase === 'done'}>
        <div className="intro-sparkle-field" aria-hidden="true" />
        <img src="/CircleStudios_Stacked.png" alt="" className="hero-intro-logo" />
      </div>

      <div className={`hero-content ${introPhase === 'transition' ? 'hero-intro-reveal hero-content-growing' : ''} ${introPhase === 'done' ? 'hero-intro-reveal hero-content-settled' : 'hero-intro-hidden'}`}>
        <div className="hero-eyebrow" data-testid="hero-logo">
          <img src="/CircleStudios_Stacked.png" alt="Circle Studios" className={`hero-logo-img ${introPhase === 'transition' || introPhase === 'done' ? 'hero-logo-settled' : 'hero-logo-pre'}`} />
          <div
            className="hero-logo-shimmer"
            aria-hidden="true"
            style={{
              WebkitMaskImage: `url(/CircleStudios_Stacked.png)`,
              maskImage: `url(/CircleStudios_Stacked.png)`,
            }}
          />
        </div>
        <div className="hero-rule" />
        <h1 className={`hero-headline ${introPhase === 'transition' || introPhase === 'done' ? 'hero-text-in' : ''}`} data-testid="hero-headline" style={bts('headline')}>
          <span className="headline-line headline-line-1">{hero.headlineLine1 || 'Where'}</span>
          <span className="headline-line headline-line-2">{hero.headlineLine2 || 'Experiences'}</span>
          <span className="headline-line headline-line-3">{hero.headlineLine3 || 'Become Influence'}</span>
        </h1>
        <p className={`hero-subheadline ${introPhase === 'transition' || introPhase === 'done' ? 'hero-text-in' : ''}`} data-testid="hero-subheadline" style={bts('subheadline')}>{hero.subheadline || DEFAULT_CONTENT.hero.subheadline}</p>
        <div className={`hero-cta-row ${introPhase === 'transition' || introPhase === 'done' ? 'hero-text-in' : ''}`} data-testid="hero-cta-row">
          <a href={hero.ctaPrimaryLink || '#gallery'} className="hero-cta hero-cta-primary" data-testid="hero-cta-gallery" style={bts('ctaButtons')} onClick={(e) => { const link = hero.ctaPrimaryLink || '#gallery'; if (link.startsWith('#')) { e.preventDefault(); document.getElementById(link.slice(1))?.scrollIntoView({ behavior: 'smooth' }); } }}>{hero.ctaPrimaryText || 'Our Work'}</a>
          <a href={hero.ctaSecondaryLink || '#contact'} className="hero-cta hero-cta-secondary" data-testid="hero-cta-contact" style={bts('ctaButtons')} onClick={(e) => { const link = hero.ctaSecondaryLink || '#contact'; if (link.startsWith('#')) { e.preventDefault(); document.getElementById(link.slice(1))?.scrollIntoView({ behavior: 'smooth' }); } }}>{hero.ctaSecondaryText || 'Connect With Us'}</a>
        </div>
      </div>

    </section>
  );
}

// ─── Section 2: Capabilities ────────────────────────────────
function CapabilitiesVideoPlayer({ videoSrc }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasUnmutedOnce, setHasUnmutedOnce] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimer = useRef(null);

  const API = process.env.REACT_APP_BACKEND_URL || '';
  const fullSrc = videoSrc && videoSrc.startsWith('/') ? `${API}${videoSrc}` : videoSrc;

  // IntersectionObserver for autoplay/pause
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInViewport(entry.isIntersecting),
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isInViewport) {
      video.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, [isInViewport]);

  // Time update for progress bar
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => setProgress(video.currentTime);
    const onMeta = () => setDuration(video.duration || 0);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('loadedmetadata', onMeta);
    return () => { video.removeEventListener('timeupdate', onTime); video.removeEventListener('loadedmetadata', onMeta); };
  }, []);

  // Auto-hide controls
  const scheduleHide = useCallback(() => {
    clearTimeout(hideTimer.current);
    setShowControls(true);
    hideTimer.current = setTimeout(() => setShowControls(false), 2500);
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) { video.play().then(() => setIsPlaying(true)).catch(() => {}); }
    else { video.pause(); setIsPlaying(false); }
    scheduleHide();
  };

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;
    if (!hasUnmutedOnce) {
      video.currentTime = 0;
      video.muted = false;
      video.play().then(() => setIsPlaying(true)).catch(() => {});
      setIsMuted(false);
      setHasUnmutedOnce(true);
    } else {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
    scheduleHide();
  };

  const handleScrub = (e) => {
    const video = videoRef.current;
    const bar = e.currentTarget;
    if (!video || !bar) return;
    const rect = bar.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    video.currentTime = (x / rect.width) * (duration || 1);
    scheduleHide();
  };

  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <div
      className="cap-video-container"
      ref={containerRef}
      onMouseMove={scheduleHide}
      onMouseEnter={scheduleHide}
      data-testid="capabilities-video"
    >
      <video
        ref={videoRef}
        src={fullSrc}
        muted={isMuted}
        loop
        playsInline
        preload="metadata"
        className="cap-video-el"
      />

      {/* Sound toggle — top right with pulse */}
      <button
        className={`cap-sound-btn ${!isMuted ? 'cap-sound-on' : 'cap-sound-pulse'}`}
        onClick={toggleSound}
        aria-label={isMuted ? 'Unmute' : 'Mute'}
        data-testid="cap-sound-btn"
      >
        {isMuted ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M11 5L6 9H2v6h4l5 4V5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M11 5L6 9H2v6h4l5 4V5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M15.54 8.46a5 5 0 010 7.07" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M19.07 4.93a10 10 0 010 14.14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
        )}
      </button>

      {/* Controls bar — bottom */}
      <div className={`cap-controls ${showControls || !isPlaying ? 'cap-controls-visible' : ''}`}>
        <button className="cap-play-btn" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'} data-testid="cap-play-btn">
          {isPlaying ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
          )}
        </button>
        <div className="cap-progress-bar" onClick={handleScrub} data-testid="cap-progress-bar">
          <div className="cap-progress-track" />
          <div className="cap-progress-fill" style={{ width: `${pct}%` }} />
          <div className="cap-progress-thumb" style={{ left: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

// ─── Animated Counter Hook ───────────────────────────────────
function useCounter(target, inView, duration = 2000) {
  const [count, setCount] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, target, duration]);
  return count;
}

// ─── Impact Transition — Creative stats bridge between About & Capabilities ──
function ImpactTransition({ capVideoSrc, statsContent, typo }) {
  const t = typo || {};
  const bts = (k) => { if (!t[k]) return {}; const s = {}; if (t[k].fontFamily) s.fontFamily = t[k].fontFamily; if (t[k].fontSize) s.fontSize = t[k].fontSize; if (t[k].fontWeight) s.fontWeight = t[k].fontWeight; if (t[k].textTransform) s.textTransform = t[k].textTransform; if (t[k].letterSpacing) s.letterSpacing = t[k].letterSpacing; if (t[k].lineHeight) s.lineHeight = t[k].lineHeight; return s; };
  const [ref, inView] = useInView({ threshold: 0.2 });
  const ds = DEFAULT_CONTENT.capabilities.stats;
  const s = statsContent || ds;
  const s0 = s[0] || ds[0]; const s1 = s[1] || ds[1]; const s2 = s[2] || ds[2]; const s3 = s[3] || ds[3];
  const c0 = useCounter(s0.num, inView, 4000);
  const c1 = useCounter(s1.num, inView, 3500);
  const c2 = useCounter(s2.num, inView, 3800);
  const c3 = useCounter(s3.num, inView, 4200);

  const stats = [
    { num: c0, suffix: s0.suffix, label: s0.label },
    { num: c1, suffix: s1.suffix, label: s1.label },
    { num: c2, suffix: s2.suffix, label: s2.label },
    { num: c3, suffix: s3.suffix, label: s3.label },
  ];

  return (
    <section className="impact-transition" ref={ref} data-testid="impact-transition">
      <div className="impact-scanline" aria-hidden="true" />
      {capVideoSrc && (
        <div className="impact-video-wrap">
          <CapabilitiesVideoPlayer videoSrc={capVideoSrc} />
        </div>
      )}
      <div className="impact-inner">
        {stats.map((s, i) => (
          <div
            className={`impact-stat ${inView ? 'animate-slide-up' : 'pre-animate'}`}
            key={i}
            style={{ animationDelay: `${i * 150}ms` }}
            data-testid={`impact-stat-${i}`}
          >
            <span className="impact-stat-label" style={bts('statLabel')}>{s.label}</span>
            <span className="impact-stat-num" style={bts('statNumber')}>{s.num}{s.suffix}</span>
          </div>
        ))}
      </div>
      <div className="impact-edge impact-edge-top" aria-hidden="true" />
      <div className="impact-edge impact-edge-bottom" aria-hidden="true" />
    </section>
  );
}


function CapabilitiesSection({ content, typo }) {
  const t = typo || {};
  const bts = (k) => { if (!t[k]) return {}; const s = {}; if (t[k].fontFamily) s.fontFamily = t[k].fontFamily; if (t[k].fontSize) s.fontSize = t[k].fontSize; if (t[k].fontWeight) s.fontWeight = t[k].fontWeight; if (t[k].textTransform) s.textTransform = t[k].textTransform; if (t[k].letterSpacing) s.letterSpacing = t[k].letterSpacing; if (t[k].lineHeight) s.lineHeight = t[k].lineHeight; return s; };
  const [ref, inView] = useInView();
  const data = content || DEFAULT_CONTENT.capabilities;
  const items = data.items || DEFAULT_CONTENT.capabilities.items;
  const visibleItems = useStagger(inView, items.length, 120);

  return (
    <section className="capabilities-section" id="capabilities" data-testid="capabilities-section" ref={ref}>
      <div className="cs-container">
        <p className={`section-label ${inView ? 'animate-fade-in' : 'pre-animate'}`}></p>
        <ScrollRevealText text={data.title || 'Capabilities'} tag="h2" className="section-title section-title-dark" style={bts('title')} />
        <p className={`section-subtitle ${inView ? 'animate-slide-up' : 'pre-animate'}`} style={bts('subtitle')}>
          {data.subtitle || DEFAULT_CONTENT.capabilities.subtitle}
        </p>

        <div className="capabilities-grid" data-testid="capabilities-grid">
          {items.map((item, idx) => (
            <div
              className={`capability-card ${visibleItems.includes(idx) ? 'animate-slide-up' : 'pre-animate'}`}
              key={idx}
              style={{ animationDelay: `${idx * 100}ms` }}
              data-testid={`capability-card-${idx}`}
            >
              <div className="cap-card-body">
                <h3 className="capability-title" style={bts('cardTitle')}>{item.title}</h3>
                <ul className="capability-bullets" style={bts('cardBullets')}>
                  {(item.bullets && item.bullets.length > 0
                    ? item.bullets
                    : (item.text || '').split(/,\s*(?:and\s+)?/).filter(Boolean).map(p => {
                        const c = p.replace(/\.$/, '').trim();
                        return c.charAt(0).toUpperCase() + c.slice(1);
                      })
                  ).map((point, j) => (
                    <li key={j} className="capability-bullet">{point}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section: Sustainability & Social Responsibility ────────
const RETREAD_IMG = 'https://customer-assets.emergentagent.com/job_e157f8b6-f50b-488c-8623-9c43c798a131/artifacts/6alpyjqc_3863D0F9-8AF2-4565-8A76-99DD00ABC4F4.jpeg';

function VideoShowcase({ platform, onClose }) {
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (platform) {
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [platform]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !platform) return;
    video.currentTime = 0;
    video.play().catch(() => {});
  }, [platform]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 500);
  };

  if (!platform) return null;
  const videoSrc = platform.video?.startsWith('/') ? `${API}${platform.video}` : platform.video;

  return (
    <div
      className={`vshowcase ${visible ? 'vshowcase-open' : ''}`}
      onClick={handleClose}
      data-testid="video-showcase"
    >
      <div className="vshowcase-cinema" onClick={(e) => e.stopPropagation()}>
        <video
          ref={videoRef}
          src={videoSrc}
          muted
          loop
          playsInline
          autoPlay
          className="vshowcase-video"
        />
        <div className="vshowcase-vignette" />
      </div>
      <div className="vshowcase-chrome">
        <button className="vshowcase-close" onClick={handleClose} data-testid="video-showcase-close" aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </button>
        <div className="vshowcase-title-bar">
          <span className="vshowcase-title">{platform.name}</span>
          <span className="vshowcase-hint">Tap anywhere to close</span>
        </div>
      </div>
    </div>
  );
}

function StixVideoBackground({ src }) {
  const videoRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL || '';
  const fullSrc = src && src.startsWith('/') ? `${API}${src}` : src;
  const prevSrc = useRef(fullSrc);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !fullSrc) return;
    if (prevSrc.current !== fullSrc) {
      prevSrc.current = fullSrc;
      video.load();
    }
    const tryPlay = () => { if (video.paused) video.play().catch(() => {}); };
    video.addEventListener('canplay', tryPlay);
    video.addEventListener('loadeddata', tryPlay);
    tryPlay();
    return () => { video.removeEventListener('canplay', tryPlay); video.removeEventListener('loadeddata', tryPlay); };
  }, [fullSrc]);

  if (!fullSrc) return null;
  return (
    <div className="stix-video-bg" data-testid="stix-video-bg">
      <video
        ref={videoRef}
        src={fullSrc}
        muted
        loop
        playsInline
        autoPlay
        className="stix-video-el"
      />
      <div className="stix-video-overlay" />
    </div>
  );
}

function SustainabilitySection({ content, typo }) {
  const t = typo || {};
  const bts = (k) => { if (!t[k]) return {}; const s = {}; if (t[k].fontFamily) s.fontFamily = t[k].fontFamily; if (t[k].fontSize) s.fontSize = t[k].fontSize; if (t[k].fontWeight) s.fontWeight = t[k].fontWeight; if (t[k].textTransform) s.textTransform = t[k].textTransform; if (t[k].letterSpacing) s.letterSpacing = t[k].letterSpacing; if (t[k].lineHeight) s.lineHeight = t[k].lineHeight; return s; };
  const [ref, inView] = useInView({ threshold: 0.05 });
  const [hoveredPlatform, setHoveredPlatform] = useState(null);
  const [showcasePlatform, setShowcasePlatform] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL || '';
  const susData = content || DEFAULT_CONTENT.sustainability;
  const susTitle = susData.title || DEFAULT_CONTENT.sustainability.title;
  const susIntro = susData.intro || DEFAULT_CONTENT.sustainability.intro;
  const retreadPlatformVideo = resolveMedia(susData.retreadPlatformVideo, `${API}/api/uploads/stix_exhibit_bg.mp4`);
  const stixPlatformVideo = resolveMedia(susData.stixPlatformVideo, `${API}/api/uploads/stix_minecraft_bg.mp4`);

  const impactItems = susData.impactItems || DEFAULT_CONTENT.sustainability.impactItems;
  const platforms = (susData.platforms || DEFAULT_CONTENT.sustainability.platforms).map(p => ({
    ...p,
    video: p.id === 'stix' ? stixPlatformVideo : retreadPlatformVideo,
  }));

  const a = (delay = 0) => inView ? 'sus-visible' : 'sus-hidden';

  return (
    <section className="sus" id="sustainability" data-testid="sustainability-section" ref={ref}>

      {/* ── UNIFIED SUSTAINABILITY HERO + IMPACT ── */}
      <div className="sus-hero">
        <div className="sus-hero-grid" aria-hidden="true" />
        <div className="sus-hero-glow" aria-hidden="true" />
        <div className="sus-hero-sweep" aria-hidden="true" />
        <div className={`sus-hero-inner ${inView ? 'sus-visible' : 'sus-hidden'}`}>
          <h2 className="section-title section-title-dark" style={{ textAlign: 'left', marginBottom: '24px', ...bts('title') }}>
            {susTitle}
          </h2>
          <p className="sus-intro" style={bts('intro')}>
            {susIntro}
          </p>
        </div>

        {/* Impact cards sit INSIDE the hero for unified feel */}
        <div className={`sus-impact-unified ${inView ? 'sus-visible' : 'sus-hidden'}`} style={{ animationDelay: '0.3s' }}>
          <span className="sus-eyebrow sus-eyebrow-sm">Measurable Impact</span>
          <div className="sus-impact-grid">
            {impactItems.map((item, idx) => (
              <div key={idx} className={`sus-impact-card ${inView ? 'sus-visible' : 'sus-hidden'}`} style={{ animationDelay: `${0.4 + 0.08 * idx}s` }} data-testid={`sust-impact-${idx}`}>
                <span className="sus-imp-num">{item.icon}</span>
                <div className="sus-imp-body">
                  <span className="sus-imp-stat" style={bts('metricValue')}>{item.stat}</span>
                  <h4 className="sus-imp-label" style={bts('metricLabel')}>{item.label}</h4>
                  <p className="sus-imp-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SYSTEM STORY GROUP ── */}
      <div className="sus-story-group">

      {/* ── PLATFORM REVEAL ── */}
      <div className="sus-platforms">
        <span className="sus-eyebrow sus-eyebrow-sm">System Platforms</span>
        <div className="sus-platform-grid">
          {platforms.map((p) => (
            <div
              key={p.id}
              className={`sus-plat-card sus-plat-${p.accent} ${inView ? 'sus-visible' : 'sus-hidden'}`}
              data-testid={`sust-${p.id}-card`}
              onMouseEnter={() => setHoveredPlatform(p.id)}
              onMouseLeave={() => setHoveredPlatform(null)}
              onClick={() => p.video && setShowcasePlatform({ name: p.name, video: p.video, accent: p.accent })}
              style={{ cursor: p.video ? 'pointer' : 'default' }}
            >
              {p.video && <StixVideoBackground src={p.video} />}
              <div className="sus-plat-edge" />
              <div className="sus-plat-body">
                <h3 className="sus-plat-name" style={bts('platformTitle')}>{p.name}</h3>
                <p className="sus-plat-tag" style={bts('platformBody')}>{p.tagline}</p>
                <ul className="sus-plat-list">
                  {p.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
              <div className={`sus-plat-glow ${hoveredPlatform === p.id ? 'sus-glow-on' : ''}`} />
              <div className="sus-plat-shine" />
              {p.video && (
                <div className="sus-plat-play-hint">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>
                  <span>Watch</span>
                </div>
              )}
            </div>
          ))}
        </div>
        {/* ── Video Showcase Theater ── */}
        <VideoShowcase platform={showcasePlatform} onClose={() => setShowcasePlatform(null)} />
      </div>

      </div>{/* end .sus-story-group */}

    </section>
  );
}

// ─── Engine Cinematic Carousel ──────────────────────────────
const ENGINE_CAROUSEL_IMAGES = [
  { url: 'https://images.unsplash.com/photo-1765278248866-979e9511be24?w=900&q=80', alt: 'Stage production lighting' },
  { url: 'https://images.pexels.com/photos/10508110/pexels-photo-10508110.jpeg?auto=compress&cs=tinysrgb&w=900', alt: 'Immersive digital exhibition' },
  { url: 'https://images.unsplash.com/photo-1765277873864-5234fa063cab?w=900&q=80', alt: 'Arena stage setup' },
  { url: 'https://images.unsplash.com/photo-1761052677126-2c7138069d44?w=900&q=80', alt: 'Fashion displays at night' },
  { url: 'https://images.unsplash.com/photo-1766019463451-04e0b1269f37?w=900&q=80', alt: 'Control desk production' },
  { url: 'https://images.pexels.com/photos/12334547/pexels-photo-12334547.jpeg?auto=compress&cs=tinysrgb&w=900', alt: 'Art installation' },
  { url: 'https://images.unsplash.com/photo-1765344550212-a3b963d63c32?w=900&q=80', alt: 'Concert filming' },
  { url: 'https://images.unsplash.com/photo-1761225646548-bc92fea0dc72?w=900&q=80', alt: 'Modern display' },
  { url: 'https://images.pexels.com/photos/16120243/pexels-photo-16120243.jpeg?auto=compress&cs=tinysrgb&w=900', alt: 'Premium event venue' },
  { url: 'https://images.pexels.com/photos/33565506/pexels-photo-33565506.jpeg?auto=compress&cs=tinysrgb&w=900', alt: 'Live concert production' },
];

function EngineCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState({});
  const total = ENGINE_CAROUSEL_IMAGES.length;
  const intervalRef = useRef(null);

  const advance = useCallback((dir = 1) => {
    setActive((prev) => (prev + dir + total) % total);
  }, [total]);

  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(() => advance(1), 2500);
    return () => clearInterval(intervalRef.current);
  }, [paused, advance]);

  const getSlotClass = (idx) => {
    const diff = ((idx - active) % total + total) % total;
    if (diff === 0) return 'ec-center';
    if (diff === 1 || diff === total - 1) return diff === 1 ? 'ec-right' : 'ec-left';
    if (diff === 2 || diff === total - 2) return diff === 2 ? 'ec-far-right' : 'ec-far-left';
    return 'ec-hidden';
  };

  return (
    <div
      className="engine-carousel"
      data-testid="engine-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="ec-viewport">
        <div className="ec-track">
          {ENGINE_CAROUSEL_IMAGES.map((img, idx) => (
            <div
              key={idx}
              className={`ec-slide ${getSlotClass(idx)}`}
              data-testid={`ec-slide-${idx}`}
            >
              <div className={`ec-img-wrap ec-kb-${idx % 4}`}>
                {(Math.abs(((idx - active) % total + total) % total) <= 3 ||
                  total - Math.abs(((idx - active) % total + total) % total) <= 3) && (
                  <img
                    src={img.url}
                    alt={img.alt}
                    loading="lazy"
                    onLoad={() => setLoaded((p) => ({ ...p, [idx]: true }))}
                    className={`ec-img ${loaded[idx] ? 'ec-img-loaded' : ''}`}
                  />
                )}
              </div>
              <div className="ec-overlay" />
            </div>
          ))}
        </div>
        <div className="ec-vignette" />
      </div>
      <button
        className="ec-arrow ec-arrow-left"
        onClick={() => advance(-1)}
        aria-label="Previous"
        data-testid="ec-arrow-left"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <button
        className="ec-arrow ec-arrow-right"
        onClick={() => advance(1)}
        aria-label="Next"
        data-testid="ec-arrow-right"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div className="ec-counter">
        <span className="ec-counter-current">{String(active + 1).padStart(2, '0')}</span>
        <span className="ec-counter-sep">/</span>
        <span className="ec-counter-total">{String(total).padStart(2, '0')}</span>
      </div>
    </div>
  );
}

// ─── Section 3: Omni-Channel Experience Engine ──────────────
function OmniChannelEngine({ content }) {
  const [ref, inView] = useInView();
  const data = content || DEFAULT_CONTENT.engine;
  const steps = data.steps || DEFAULT_CONTENT.engine.steps;
  const visibleItems = useStagger(inView, steps.length, 150);

  // Handle both old (string array) and new (object array) formats
  const normalizedSteps = steps.map((s) =>
    typeof s === 'string' ? { name: s, text: '' } : s
  );

  return (
    <section className="engine-section" id="engine" data-testid="omni-channel-experience-engine" ref={ref}>
      <FloatingParticles count={10} className="section-particles" />
      <div className="cs-container">
        <div className={`engine-header ${inView ? 'animate-slide-up' : 'pre-animate'}`}>
          <p className="section-label">The System</p>
          <h2 className="section-title">{data.title || DEFAULT_CONTENT.engine.title}</h2>
          <p className="engine-subtitle-text">{data.subtitle || DEFAULT_CONTENT.engine.subtitle}</p>
          <p className="engine-intro">{data.intro || DEFAULT_CONTENT.engine.intro}</p>
        </div>

        <EngineCarousel />

        <div className="engine-steps-grid">
          {normalizedSteps.map((step, idx) => (
            <div
              className={`engine-step-card ${visibleItems.includes(idx) ? 'animate-scale-in' : 'pre-animate-scale'}`}
              key={idx}
              data-testid={`engine-step-${idx}`}
            >
              <div className="engine-step-number">{String(idx + 1).padStart(2, '0')}</div>
              <h3 className="engine-step-name">{step.name}</h3>
              {step.text && <p className="engine-step-text">{step.text}</p>}
              {idx < normalizedSteps.length - 1 && (
                <div className="engine-step-connector" aria-hidden="true">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={`engine-outcome ${inView ? 'animate-fade-in' : 'pre-animate'}`} data-testid="engine-outcome">
          <div className="engine-outcome-line" />
          <p className="engine-outcome-text">{data.outcome || DEFAULT_CONTENT.engine.outcome}</p>
        </div>
      </div>
    </section>
  );
}

// ─── Section 4: Sectors ─────────────────────────────────────
function SectorsSection({ content }) {
  const [ref, inView] = useInView();
  const data = content || DEFAULT_CONTENT.sectors;

  // Handle both old array format and new object format
  let sectorItems, sectionTitle, sectionSubtitle;
  if (Array.isArray(data)) {
    sectorItems = data.map((s) => (typeof s === 'string' ? s : s.title));
    sectionTitle = 'Sectors';
    sectionSubtitle = DEFAULT_CONTENT.sectors.subtitle;
  } else {
    sectorItems = data.items || DEFAULT_CONTENT.sectors.items;
    sectionTitle = data.title || DEFAULT_CONTENT.sectors.title;
    sectionSubtitle = data.subtitle || DEFAULT_CONTENT.sectors.subtitle;
  }

  const visibleItems = useStagger(inView, sectorItems.length, 120);

  return (
    <section className="sectors-section" id="sectors" data-testid="sectors-section" ref={ref}>
      <div className="cs-container">
        <p className={`section-label section-label-dark ${inView ? 'animate-fade-in' : 'pre-animate'}`}>Industries</p>
        <h2 className={`section-title section-title-dark ${inView ? 'animate-slide-up' : 'pre-animate'}`}>
          {sectionTitle}
        </h2>
        <p className={`section-subtitle ${inView ? 'animate-slide-up' : 'pre-animate'}`}>
          {sectionSubtitle}
        </p>
        <div className="sectors-grid">
          {sectorItems.map((item, idx) => (
            <div
              className={`sector-item ${visibleItems.includes(idx) ? 'animate-slide-up' : 'pre-animate'}`}
              key={idx}
              style={{ animationDelay: `${idx * 100}ms` }}
              data-testid={`sector-item-${idx}`}
            >
              <span className="sector-item-num">{String(idx + 1).padStart(2, '0')}</span>
              <div className="sector-item-line" />
              <span className="sector-item-text">{typeof item === 'string' ? item : item.title || item}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section 5: Clients ─────────────────────────────────────
function ClientsSection({ content }) {
  const [ref, inView] = useInView();
  const data = content || DEFAULT_CONTENT.clients;

  const partners = [
    { name: 'NASA', color: '#E03C31' },
    { name: 'Toyota', color: '#EB0A1E' },
    { name: 'Coca-Cola', color: '#F40009' },
    { name: 'Adidas', color: '#FFFFFF' },
    { name: 'Ford', color: '#003399' },
    { name: 'Sony', color: '#FFFFFF' },
    { name: 'Pepsi', color: '#004B93' },
    { name: 'Adobe', color: '#FF0000' },
    { name: 'AT&T', color: '#009FDB' },
    { name: 'NBC Sports', color: '#E5A100' },
    { name: 'Chevrolet', color: '#D4AF37' },
    { name: 'Live Nation', color: '#E12726' },
  ];

  return (
    <section className="clients-section" id="clients" data-testid="clients-section" ref={ref}>
      <div className="clients-ambient" aria-hidden="true" />
      <div className="cs-container">
        <div className={`clients-content ${inView ? 'animate-slide-up' : 'pre-animate'}`}>
          <p className="section-label">Trusted Partners</p>
          <h2 className="section-title">{data.title || DEFAULT_CONTENT.clients.title}</h2>
          <p className="clients-subtitle">{data.subtitle || DEFAULT_CONTENT.clients.subtitle}</p>
        </div>
        <div className={`clients-logo-grid ${inView ? 'animate-fade-in' : 'pre-animate'}`} data-testid="clients-grid">
          {partners.map((p, idx) => (
            <div
              className="client-logo-card"
              key={idx}
              data-testid={`client-logo-${idx}`}
              style={{ '--brand-color': p.color }}
            >
              <span className="client-logo-text">{p.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section 6: Our Work ────────────────────────────────────
function OurWorkSection({ content }) {
  const [ref, inView] = useInView();
  const data = content || DEFAULT_CONTENT.work;
  const platforms = data.platforms || DEFAULT_CONTENT.work.platforms;
  const signatureItems = data.signatureItems || DEFAULT_CONTENT.work.signatureItems;
  const visibleItems = useStagger(inView, platforms.length, 150);

  return (
    <section className="work-section" id="work" data-testid="work-section" ref={ref}>
      <div className="cs-container">
        <p className={`section-label ${inView ? 'animate-fade-in' : 'pre-animate'}`}>Portfolio</p>
        <h2 className={`section-title ${inView ? 'animate-slide-up' : 'pre-animate'}`}>Our Work</h2>
        <p className={`work-intro ${inView ? 'animate-slide-up' : 'pre-animate'}`}>
          {data.intro || DEFAULT_CONTENT.work.intro}
        </p>

        <div className={`signature-positioning ${inView ? 'animate-fade-in' : 'pre-animate'}`} data-testid="signature-positioning">
          <p className="signature-label">{data.signatureLabel || 'Our work is defined by:'}</p>
          <div className="signature-items">
            {signatureItems.map((item, idx) => (
              <span className="signature-item" key={idx} data-testid={`signature-item-${idx}`}>{item}</span>
            ))}
          </div>
        </div>

        <div className="work-platforms-grid">
          {platforms.map((platform, idx) => {
            const videoUrl = resolveMedia(platform.video, '');
            return (
              <div
                className={`work-platform-tile ${visibleItems.includes(idx) ? 'animate-scale-in' : 'pre-animate-scale'}`}
                key={idx}
                data-testid={`work-platform-tile-${idx}`}
              >
                {videoUrl && (
                  <video
                    src={videoUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="work-tile-video"
                  />
                )}
                <div className="work-tile-overlay" />
                <div className="work-tile-content">
                  <span className="work-tile-title">{platform.name}</span>
                  <span className="work-tile-prompt">{platform.prompt}</span>
                </div>
                <div className="work-tile-shimmer" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Trusted By Logo Bar ─────────────────────────────────────
function TrustedByBar({ content }) {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const data = content || DEFAULT_CONTENT.trustedPartners;
  const logos = data.logos || DEFAULT_CONTENT.trustedPartners.logos;
  const API = process.env.REACT_APP_BACKEND_URL || '';
  // Triple the logos for seamless infinite scroll
  const track = [...logos, ...logos, ...logos, ...logos];
  return (
    <div className="trusted-bar" ref={ref} data-testid="trusted-bar">
      <div className={`trusted-inner ${inView ? 'animate-fade-in' : 'pre-animate'}`}>
        <div className="trusted-marquee-wrap">
          <div className="trusted-marquee trusted-row-1">
            {track.map((logo, i) => (
              <div key={i} className="trusted-logo-img-wrap" data-testid={`trusted-logo-${i}`}>
                <img src={logo.src && logo.src.startsWith('/api') ? `${API}${logo.src}` : logo.src} alt={logo.alt} className="trusted-logo-img" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Event / Location Logo Scroll (Reverse Direction) ────────
function EventLogosBar({ content }) {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const data = content || DEFAULT_CONTENT.eventLogos;
  const logos = data.logos || DEFAULT_CONTENT.eventLogos.logos;
  const track = [...logos, ...logos, ...logos, ...logos];
  return (
    <div className="event-bar" ref={ref} data-testid="event-bar">
      <div className={`trusted-inner ${inView ? 'animate-fade-in' : 'pre-animate'}`}>
        <div className="trusted-marquee-wrap">
          <div className="trusted-marquee event-row-1">
            {track.map((logo, i) => (
              <div key={i} className="trusted-logo-img-wrap" data-testid={`event-logo-${i}`}>
                <img src={logo.src} alt={logo.alt} className="trusted-logo-img" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Section 7: About ───────────────────────────────────────
function AboutSection({ content, typo }) {
  const t = typo || {};
  const bts = (k) => { if (!t[k]) return {}; const s = {}; if (t[k].fontFamily) s.fontFamily = t[k].fontFamily; if (t[k].fontSize) s.fontSize = t[k].fontSize; if (t[k].fontWeight) s.fontWeight = t[k].fontWeight; if (t[k].textTransform) s.textTransform = t[k].textTransform; if (t[k].letterSpacing) s.letterSpacing = t[k].letterSpacing; if (t[k].lineHeight) s.lineHeight = t[k].lineHeight; return s; };
  const [ref, inView] = useInView();
  const data = content || DEFAULT_CONTENT.about;
  const philosophy = data.philosophy || DEFAULT_CONTENT.about.philosophy;

  return (
    <section className="about-section" id="about" data-testid="about-section" ref={ref}>
      <div className="about-grid" aria-hidden="true" />
      <div className="about-glow" aria-hidden="true" />
      <div className="about-sweep" aria-hidden="true" />
      <div className="cs-container">
        <div className={`about-content ${inView ? 'animate-slide-up' : 'pre-animate'}`}>
          <p className="section-label section-label-dark"></p>
          <h2 className={`about-gradient-title ${inView ? 'animate-slide-up' : 'pre-animate'}`} data-testid="about-title" style={bts('title')}>
            {data.title || DEFAULT_CONTENT.about.title}
          </h2>
          <p className="about-text" style={bts('body')}>{data.text || DEFAULT_CONTENT.about.text}</p>
          <div className="about-philosophy" data-testid="about-philosophy">
            {philosophy.map((line, idx) => (
              <p className="philosophy-line" key={idx} data-testid={`philosophy-line-${idx}`} style={bts('philosophyLines')}>{line}</p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Proprietary Engagements ────────────────────────
function ProprietaryEngagements({ content, typo }) {
  const t = typo || {};
  const bts = (k) => { if (!t[k]) return {}; const s = {}; if (t[k].fontFamily) s.fontFamily = t[k].fontFamily; if (t[k].fontSize) s.fontSize = t[k].fontSize; if (t[k].fontWeight) s.fontWeight = t[k].fontWeight; if (t[k].textTransform) s.textTransform = t[k].textTransform; if (t[k].letterSpacing) s.letterSpacing = t[k].letterSpacing; if (t[k].lineHeight) s.lineHeight = t[k].lineHeight; return s; };
  const [ref, inView] = useInView({ threshold: 0.2 });
  const data = content || {};

  // Canonical URLs — used as ground truth if DB/CMS value is missing or '#'
  const CANONICAL_URLS = {
    SEICON: 'https://sei-con.org/',
    FANPRIX: 'https://thefanprix.com/',
    OZFEST: 'https://premium-ozfest.emergent.host',
  };

  const defaultCards = [
    { name: 'SEICON', href: CANONICAL_URLS.SEICON, color: '#E8443A', accent: '#FF6B5E', visible: true },
    { name: 'FANPRIX', href: CANONICAL_URLS.FANPRIX, color: '#E8A817', accent: '#FFD040', visible: true },
    { name: 'OZFEST', href: CANONICAL_URLS.OZFEST, color: '#10B9A0', accent: '#34D3B8', visible: true },
  ];

  const sectionLabel = data.sectionLabel || 'Explore';
  const sectionTitle = data.sectionTitle || 'Proprietary Engagements';
  const rawCards = (data.cards || defaultCards).filter(c => c.visible !== false);
  // Patch any broken hrefs — if '#' or empty, use canonical URL
  const microsites = rawCards.map(card => ({
    ...card,
    href: (!card.href || card.href === '#') ? (CANONICAL_URLS[card.name] || '#') : card.href,
  }));

  return (
    <section className="prop-section" ref={ref} data-testid="proprietary-engagements">
      <div className="prop-glow-orb prop-glow-1" aria-hidden="true" />
      <div className="prop-glow-orb prop-glow-2" aria-hidden="true" />
      <div className="cs-container">
        <span className={`section-label ${inView ? 'animate-slide-up' : 'pre-animate'}`} style={bts('label')}>{sectionLabel}</span>
        <h2 className={`section-title ${inView ? 'animate-slide-up' : 'pre-animate'}`} style={{ animationDelay: '100ms', ...bts('title') }}>
          {sectionTitle}
        </h2>
        <div className="prop-grid">
          {microsites.map((site, i) => (
            <a
              key={site.name}
              href={site.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`prop-card ${inView ? 'animate-slide-up' : 'pre-animate'}`}
              style={{
                animationDelay: `${200 + i * 150}ms`,
                '--card-color': site.color,
                '--card-accent': site.accent,
              }}
              data-testid={`prop-card-${site.name.toLowerCase()}`}
            >
              <div className="prop-card-glow" aria-hidden="true" />
              <div className="prop-card-border" aria-hidden="true" />
              <span className="prop-card-name" style={bts('cardTitle')}>{site.name}</span>
              <svg className="prop-card-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M17 7H7M17 7V17"/>
              </svg>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section 8: Contact ─────────────────────────────────────
function ContactSection({ content, typo }) {
  const t = typo || {};
  const bts = (k) => { if (!t[k]) return {}; const s = {}; if (t[k].fontFamily) s.fontFamily = t[k].fontFamily; if (t[k].fontSize) s.fontSize = t[k].fontSize; if (t[k].fontWeight) s.fontWeight = t[k].fontWeight; if (t[k].textTransform) s.textTransform = t[k].textTransform; if (t[k].letterSpacing) s.letterSpacing = t[k].letterSpacing; if (t[k].lineHeight) s.lineHeight = t[k].lineHeight; return s; };
  const [ref, inView] = useInView();
  const data = content || DEFAULT_CONTENT.contact;

  const eyebrow = data.eyebrow || 'Start a Conversation';
  const headline = data.title || 'Ready to build\nsomething\ninfluential?';
  const btnText = data.ctaText || 'Begin Inquiry';
  const btnLink = data.ctaLink || 'mailto:info@circlestudios.com';
  const subtitle = data.subtitle || "We partner with visionaries who refuse to settle. Tell us about your ambition — we'll architect the path.";

  return (
    <section className="contact-section" id="contact" data-testid="contact-section" ref={ref}>
      <div className="contact-warm-glow" aria-hidden="true" />
      <div className="cs-container">
        <div className={`contact-content ${inView ? 'animate-slide-up' : 'pre-animate'}`}>
          <p className="contact-eyebrow" data-testid="contact-eyebrow" style={bts('eyebrow')}>{eyebrow}</p>
          <h2 className="contact-headline" data-testid="contact-headline" style={bts('headline')} dangerouslySetInnerHTML={{ __html: headline.replace(/\n/g, '<br />') }} />
          <a
            href={btnLink}
            className="contact-inquiry-btn"
            data-testid="contact-submit-btn"
            style={bts('ctaButton')}
            {...(btnLink.startsWith('http') || btnLink.startsWith('mailto') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {btnText}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7M17 7H7M17 7V17"/></svg>
          </a>
          <p className="contact-subtitle" data-testid="contact-subtitle" style={bts('subtitle')}>
            {subtitle}
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ─────────────────────────────────────────────────
function Footer({ content, typo }) {
  const t = typo || {};
  const bts = (k) => { if (!t[k]) return {}; const s = {}; if (t[k].fontFamily) s.fontFamily = t[k].fontFamily; if (t[k].fontSize) s.fontSize = t[k].fontSize; if (t[k].fontWeight) s.fontWeight = t[k].fontWeight; if (t[k].textTransform) s.textTransform = t[k].textTransform; if (t[k].letterSpacing) s.letterSpacing = t[k].letterSpacing; if (t[k].lineHeight) s.lineHeight = t[k].lineHeight; return s; };
  const data = content || DEFAULT_CONTENT.footer;

  return (
    <footer className="site-footer" data-testid="footer-section">
      <div className="footer-edge" aria-hidden="true" />
      <div className="cs-container">
        <div className="footer-inner">
          <div className="footer-brand">
            <img src={LOGO_STACKED_SRC} alt="Circle Studios" className="footer-logo" data-testid="footer-logo" />
          </div>
          <nav className="footer-nav" data-testid="footer-nav">
            {(data.navLinks || DEFAULT_CONTENT.footer.navLinks).map((link, idx) => (
              <a key={idx} href={link.href} style={bts('navLinks')} onClick={(e) => {
                if (link.href.startsWith('#')) {
                  e.preventDefault();
                  document.getElementById(link.href.slice(1))?.scrollIntoView({ behavior: 'smooth' });
                }
              }}>{link.label}</a>
            ))}
          </nav>
        </div>
        <div className="footer-social-row" data-testid="footer-social-row">
          <div className="footer-social" data-testid="footer-social">
            <a href="https://www.linkedin.com/company/circleogx/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="footer-social-icon" data-testid="social-linkedin">
              <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
            <a href="https://www.facebook.com/circleogx" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="footer-social-icon" data-testid="social-facebook">
              <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href="https://www.youtube.com/@circleogx" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="footer-social-icon" data-testid="social-youtube">
              <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            </a>
            <a href="https://www.instagram.com/circleogx" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="footer-social-icon" data-testid="social-instagram">
              <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.88 0 1.441 1.441 0 012.88 0z"/></svg>
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="footer-tagline" style={bts('tagline')}>{data.tagline || DEFAULT_CONTENT.footer.tagline}</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Section: Gallery ───────────────────────────────────────
function LazyImage({ src, alt, className, onClick, style }) {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.unobserve(el); } },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={`lazy-img-wrap ${className || ''} ${loaded ? 'lazy-loaded' : ''}`} onClick={onClick} style={style}>
      {inView && (
        <img
          src={src}
          alt={alt || ''}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          className="lazy-img"
        />
      )}
    </div>
  );
}

function GalleryLightbox({ images, currentIndex, onClose, onNav }) {
  const img = images[currentIndex];

  useEffect(() => {
    if (!img) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNav(1);
      if (e.key === 'ArrowLeft') onNav(-1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [img, onClose, onNav]);

  if (!img) return null;

  const API = process.env.REACT_APP_BACKEND_URL || '';
  const imgUrl = img.url.startsWith('/') ? `${API}${img.url}` : img.url;

  return (
    <div className="gallery-lightbox" onClick={onClose} data-testid="gallery-lightbox">
      <div className="lightbox-backdrop" />
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <img src={imgUrl} alt={img.originalName || ''} className="lightbox-img" />
        <div className="lightbox-counter">{currentIndex + 1} / {images.length}</div>
      </div>
      {currentIndex > 0 && (
        <button className="lightbox-nav lightbox-prev" onClick={(e) => { e.stopPropagation(); onNav(-1); }} aria-label="Previous" data-testid="lightbox-prev">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M15 19l-7-7 7-7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      )}
      {currentIndex < images.length - 1 && (
        <button className="lightbox-nav lightbox-next" onClick={(e) => { e.stopPropagation(); onNav(1); }} aria-label="Next" data-testid="lightbox-next">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M9 5l7 7-7 7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      )}
      <button className="lightbox-close" onClick={onClose} aria-label="Close" data-testid="lightbox-close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
      </button>
    </div>
  );
}

// ─── Full-Screen Gallery Overlay ────────────────────────────
function GalleryOverlay({ images, onClose, onImageClick, typo }) {
  const t = typo || {};
  const bts = (k) => { if (!t[k]) return {}; const s = {}; if (t[k].fontFamily) s.fontFamily = t[k].fontFamily; if (t[k].fontSize) s.fontSize = t[k].fontSize; if (t[k].fontWeight) s.fontWeight = t[k].fontWeight; if (t[k].textTransform) s.textTransform = t[k].textTransform; if (t[k].letterSpacing) s.letterSpacing = t[k].letterSpacing; if (t[k].lineHeight) s.lineHeight = t[k].lineHeight; return s; };
  const API = process.env.REACT_APP_BACKEND_URL || '';
  const BATCH_SIZE = 40;
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const scrollRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Progressive loading: render more images as user scrolls
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 600) {
        setVisibleCount(prev => Math.min(prev + BATCH_SIZE, images.length));
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [images.length]);

  const resolveImgUrl = (img) => img.url.startsWith('/') ? `${API}${img.url}` : img.url;

  return (
    <div className="gallery-overlay" data-testid="gallery-overlay" ref={scrollRef}>
      <div className="gallery-overlay-header">
        <div className="gallery-overlay-title">
          <span className="gallery-overlay-label" style={bts('overlayTitle')}>Full Collection</span>
          <span className="gallery-overlay-count" style={bts('photoCount')}>{images.length} Photos</span>
        </div>
        <button className="gallery-overlay-close" onClick={onClose} data-testid="gallery-overlay-close" aria-label="Close gallery">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>
      <div className="gallery-overlay-grid">
        {images.slice(0, visibleCount).map((img, idx) => (
          <div className="gallery-overlay-item" key={img.id || idx} onClick={() => onImageClick(idx)}>
            <img
              src={resolveImgUrl(img)}
              alt={img.originalName || `Image ${idx + 1}`}
              decoding="async"
              loading="eager"
              width={img.width || 400}
              height={img.height || 300}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function GallerySectionInner({ typo }) {
  const t = typo || {};
  const bts = (k) => { if (!t[k]) return {}; const s = {}; if (t[k].fontFamily) s.fontFamily = t[k].fontFamily; if (t[k].fontSize) s.fontSize = t[k].fontSize; if (t[k].fontWeight) s.fontWeight = t[k].fontWeight; if (t[k].textTransform) s.textTransform = t[k].textTransform; if (t[k].letterSpacing) s.letterSpacing = t[k].letterSpacing; if (t[k].lineHeight) s.lineHeight = t[k].lineHeight; return s; };
  const [ref] = useInView();

  // Frozen image data — populated exactly once on mount, never mutated again.
  // Stored as a single immutable bundle so the heroImage / previewImages /
  // filmstripImages references are stable for the lifetime of the component
  // and nothing is recomputed in the render function.
  const [frozen, setFrozen] = useState(null); // { images, heroImage, previewImages, filmstripImages, total }
  const [loading, setLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const hasLoadedRef = useRef(false);
  const PREVIEW_COUNT = 13;   // slice(1, 13) -> 12 preview tiles
  const FILMSTRIP_END = 25;   // slice(13, 25) -> 12 filmstrip thumbs (no overlap)

  // Fisher-Yates in-place shuffle. Used to randomise the preview slice on
  // every fresh page load — no sessionStorage, no persistence. Restricted
  // to a copy of the preview slice so the hero, the filmstrip, and the
  // full `images` array (used by the Full Collection modal) keep their
  // original order — modal contents are intentionally never shuffled.
  const shuffleInPlace = (arr) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const loadImages = useCallback(async () => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    setLoading(true);
    try {
      const data = await fetchGallery(1, 300, true);
      const images = data.images || [];
      // Random hero on every fresh page load — no sessionStorage, no persistence.
      // Pick one image from anywhere in the full pool; the preview + filmstrip
      // then draw from the remaining images (original order, minus the hero)
      // so no tile is ever duplicated between the hero and the grid.
      const heroIdx = images.length > 0 ? Math.floor(Math.random() * images.length) : -1;
      const heroImage = heroIdx >= 0 ? images[heroIdx] : null;
      const rest = heroIdx >= 0 ? images.filter((_, i) => i !== heroIdx) : images.slice();
      // The 12-tile preview shuffle is preserved: Fisher-Yates on a 12-slot slice.
      const previewImages = shuffleInPlace(rest.slice(0, 12));
      const filmstripImages = rest.slice(12, 24);
      // Object.freeze on the bundle and its arrays so any accidental mutation
      // anywhere in the tree throws instead of silently corrupting state.
      const bundle = Object.freeze({
        images: Object.freeze(images),
        heroImage,
        previewImages: Object.freeze(previewImages),
        filmstripImages: Object.freeze(filmstripImages),
        total: data.total,
      });
      setFrozen(bundle);
    } catch (err) {
      console.warn('Gallery load failed:', err.message);
      hasLoadedRef.current = false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadImages(); }, [loadImages]);

  // ── Live-sync on admin delete (and any other gallery mutation) ──────────
  // Listens for SSE `content-updated` events whose `section === 'gallery'`.
  // On receipt, refetches the gallery list and produces a NEW frozen bundle
  // by **diffing out removed ids only** — preserves the existing display
  // order, never re-shuffles, and never reorders surviving tiles. Newly
  // uploaded images are dropped from this update path on purpose: they are
  // handled by the existing one-shot mount fetch + sessionStorage order
  // freeze (preserving the v6 stability contract). This update path runs
  // only to remove tiles whose backing doc has been deleted.
  useEffect(() => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    let es;
    try {
      if (typeof EventSource !== 'undefined') {
        es = new EventSource(`${apiBase}/api/content/events`);
        es.addEventListener('content-updated', async (ev) => {
          let payload = {};
          try { payload = JSON.parse(ev.data || '{}'); } catch (_) { /* ignore */ }
          if (payload.section !== 'gallery') return;
          try {
            const data = await fetchGallery(1, 300, true);
            const liveIds = new Set((data.images || []).map((i) => i.id));
            setFrozen((prev) => {
              if (!prev) return prev;
              // Diff out removed ids only — do not reorder, do not insert.
              // Filter each frozen slice INDEPENDENTLY so the shuffled
              // preview order survives a delete (re-slicing from `images`
              // would un-shuffle the preview tiles).
              const survivingImages = prev.images.filter((i) => liveIds.has(i.id));
              if (survivingImages.length === prev.images.length) return prev; // no-op
              const heroImage = (prev.heroImage && liveIds.has(prev.heroImage.id)) ? prev.heroImage : (survivingImages[0] || null);
              const previewImages = prev.previewImages.filter((i) => liveIds.has(i.id));
              const filmstripImages = prev.filmstripImages.filter((i) => liveIds.has(i.id));
              return Object.freeze({
                images: Object.freeze(survivingImages),
                heroImage,
                previewImages: Object.freeze(previewImages),
                filmstripImages: Object.freeze(filmstripImages),
                total: survivingImages.length,
              });
            });
          } catch (err) { console.warn('Gallery SSE refresh failed:', err.message); }
        });
        es.onerror = () => { /* EventSource auto-reconnects */ };
      }
    } catch (_) { /* ignore */ }
    return () => { if (es) try { es.close(); } catch (_) {} };
  }, []);

  const API = process.env.REACT_APP_BACKEND_URL || '';
  const resolveUrl = useCallback((img) => img.url.startsWith('/') ? `${API}${img.url}` : img.url, [API]);

  if (!frozen && !loading) return null;
  if (!frozen) return null; // initial fetch in flight — render nothing (no flash)

  const { heroImage, previewImages, filmstripImages, total, images } = frozen;

  return (
    <section className="gallery-section" id="gallery" data-testid="gallery-section" ref={ref}>
      <div className="cs-container">
        <div className="gallery-header">
          <p className="section-label animate-fade-in"></p>
          <ScrollRevealText text="Our Work" tag="h2" className="section-title" style={bts('title')} />
        </div>
      </div>

      {/* ── Hero featured image (full-width, isolated, no jump risk) ── */}
      {heroImage && (
        <div className="gallery-stable-hero" onClick={() => setLightboxIdx(images.findIndex((i) => i.id === heroImage.id))} data-testid="gallery-hero-image">
          <img
            src={resolveUrl(heroImage)}
            alt={heroImage.originalName || 'Featured work'}
            loading="eager"
            decoding="async"
            width={heroImage.width || 1600}
            height={heroImage.height || 900}
          />
          <div className="gallery-stable-hero-overlay">
            <span className="gallery-hero-badge">Featured</span>
          </div>
        </div>
      )}

      {/* ── Uniform responsive grid: 4 cols desktop, 3 tablet, 2 mobile.
            Each tile is a fixed-aspect-ratio square — no masonry, no
            variable heights, no recalculation. Layout is deterministic at
            page-load time and never changes. ── */}
      <div className="gallery-stable-grid" data-testid="gallery-preview-grid">
        {previewImages.map((img, idx) => (
          <div
            className="gallery-stable-tile"
            key={img.id}
            onClick={() => setLightboxIdx(images.findIndex((i) => i.id === img.id))}
            data-testid={`gallery-preview-${idx}`}
          >
            <img
              src={resolveUrl(img)}
              alt={img.originalName || `Image ${idx + 2}`}
              loading="eager"
              decoding="async"
              width={img.width || 800}
              height={img.height || 800}
            />
          </div>
        ))}
      </div>

      {/* ── Explore full collection CTA (unchanged behavior) ── */}
      <div className="gallery-cta-wrap" data-testid="gallery-view-all-btn" onClick={() => setShowOverlay(true)}>
        <div className="gallery-cta-filmstrip" aria-hidden="true">
          {filmstripImages.map((img, i) => (
            <img key={i} src={resolveUrl(img)} alt="" className="gallery-cta-thumb" loading="eager" decoding="async" width={img.width || 200} height={img.height || 140} />
          ))}
          {filmstripImages.map((img, i) => (
            <img key={`d-${i}`} src={resolveUrl(img)} alt="" className="gallery-cta-thumb" loading="eager" decoding="async" width={img.width || 200} height={img.height || 140} />
          ))}
        </div>
        <div className="gallery-cta-overlay">
          <div className="gallery-cta-overlay-content">
            <span className="gallery-cta-plus">+</span>
            <span className="gallery-cta-stat">{total > 12 ? total - 12 : total}</span>
            <span className="gallery-cta-more">more photos</span>
          </div>
          <span className="gallery-cta-action">
            Explore Full Collection
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </span>
        </div>
      </div>

      {/* ── Full-screen overlay ── */}
      {showOverlay && (
        <GalleryOverlay
          images={images}
          typo={t}
          onClose={() => setShowOverlay(false)}
          onImageClick={(idx) => setLightboxIdx(idx)}
        />
      )}

      {/* ── Lightbox ── */}
      {lightboxIdx >= 0 && (
        <GalleryLightbox
          images={images}
          currentIndex={lightboxIdx}
          onClose={() => setLightboxIdx(-1)}
          onNav={(dir) => setLightboxIdx((prev) => Math.max(0, Math.min(images.length - 1, prev + dir)))}
        />
      )}
    </section>
  );
}

// ─── Homepage ───────────────────────────────────────────────
// Memoize the gallery so polling/SSE re-renders of HomePage don't cascade
// into the gallery component. Images are loaded once on mount via Fisher-Yates
// shuffle and stay locked for the rest of the session — guaranteed stable
// arrangement while the user is browsing. Custom equality: only re-render
// when the typography settings for the gallery actually change.
const GallerySection = React.memo(GallerySectionInner, (prev, next) => {
  return JSON.stringify(prev.typo || {}) === JSON.stringify(next.typo || {});
});

function HomePage() {
  const [content, setContent] = useState(null);
  const [contentLoaded, setContentLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;
    let bc = null;
    let eventSource = null;
    const API = process.env.REACT_APP_BACKEND_URL || '';

    // Preload every font in the library so any CMS typography override
    // actually renders on the visitor's browser (without this, paid/system
    // fonts silently fall back to default sans-serif and the user sees
    // no visual change after a Save).
    preloadAllFonts();

    async function loadContent(source = 'initial') {
      try {
        const data = await fetchContent();
        if (!cancelled) {
          setContent(data);
          if (source !== 'initial') {
            // eslint-disable-next-line no-console
            console.log(`[Circle Studios] Content refreshed (trigger: ${source})`);
          }
        }
      } catch (err) {
        console.warn('Using default content (API unavailable):', err.message);
      } finally {
        if (!cancelled) setContentLoaded(true);
      }
    }

    // Initial fetch
    loadContent('initial');

    // 1) Server-Sent Events — the most bulletproof channel. Server pushes
    //    directly to this tab over a persistent HTTP connection. Works
    //    cross-browser, cross-device, through proxies. Auto-reconnects.
    try {
      if (typeof EventSource !== 'undefined') {
        eventSource = new EventSource(`${API}/api/content/events`);
        eventSource.addEventListener('content-updated', () => loadContent('sse'));
        eventSource.onerror = () => {
          // EventSource auto-reconnects on its own; just log for debugging.
          // eslint-disable-next-line no-console
          console.debug('[Circle Studios] SSE connection dropped, auto-reconnecting...');
        };
        eventSource.onopen = () => {
          // eslint-disable-next-line no-console
          console.log('[Circle Studios] Live sync connected (SSE)');
        };
      }
    } catch (e) { /* ignore */ }

    // 2) BroadcastChannel — instant same-browser cross-tab.
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel('circle-studios-cms');
        bc.onmessage = (e) => {
          if (e && e.data && e.data.type === 'content-updated') {
            loadContent('broadcast');
          }
        };
      }
    } catch (e) { /* ignore */ }

    // 3) localStorage storage event — cross-tab backup channel.
    const onStorage = (e) => {
      if (e.key === 'circle-studios-cms-updated') loadContent('storage');
    };
    window.addEventListener('storage', onStorage);

    // 4) Visibility change — user switches back to the public-site tab.
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadContent('visibility');
    };
    document.addEventListener('visibilitychange', onVisible);

    // 5) Window focus — refresh when the tab regains focus.
    const onFocus = () => loadContent('focus');
    window.addEventListener('focus', onFocus);

    // 6) Polling fallback — every 15s, re-fetch to pick up anything missed.
    pollTimer = setInterval(() => loadContent('poll'), 15000);

    return () => {
      cancelled = true;
      if (eventSource) { try { eventSource.close(); } catch (e) { /* noop */ } }
      if (bc) { try { bc.close(); } catch (e) { /* noop */ } }
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      if (pollTimer) clearInterval(pollTimer);
    };
  }, []);

  const c = content ? mergeContent(content, DEFAULT_CONTENT) : DEFAULT_CONTENT;

  // Dynamic Google Font loader — loads fonts used in typography settings
  useEffect(() => {
    if (!c.typography || typeof c.typography !== 'object') return;
    const usedFamilies = new Set();
    Object.values(c.typography).forEach(section => {
      if (section && typeof section === 'object') {
        Object.values(section).forEach(el => {
          if (el?.fontFamily) usedFamilies.add(el.fontFamily);
        });
      }
    });
    const googleFonts = [];
    usedFamilies.forEach(family => {
      const match = FONT_LIBRARY.find(f => f.value === family);
      if (match?.google) googleFonts.push(match.google);
    });
    if (googleFonts.length > 0) {
      const id = 'dynamic-typo-fonts';
      let link = document.getElementById(id);
      if (!link) { link = document.createElement('link'); link.id = id; link.rel = 'stylesheet'; document.head.appendChild(link); }
      link.href = `https://fonts.googleapis.com/css2?${googleFonts.map(f => `family=${f}`).join('&')}&display=swap`;
    }
  }, [c.typography]);

  const typo = c.typography || {};

  return (
    <div className="homepage" data-testid="homepage">
      <ScrollProgress />
      <FloatingCTA />
      <SiteNav typo={typo.navigation} />
      <HeroSection content={c.hero} typo={typo.hero} />
      <TrustedByBar content={c.trustedPartners} />
      <EventLogosBar content={c.eventLogos} />
      <AboutSection content={c.about} typo={typo.about} />
      <ImpactTransition capVideoSrc={resolveMedia(c.capabilities?.video, '')} statsContent={c.capabilities?.stats} typo={typo.capabilities} />
      <CapabilitiesSection content={c.capabilities} typo={typo.capabilities} />
      <AnimatedDivider />
      <GallerySection typo={typo.gallery} />
      <div className="sus-prop-wrap">
        <SustainabilitySection content={c.sustainability} typo={typo.sustainability} />
        <ProprietaryEngagements content={c.proprietary} typo={typo.proprietary} />
      </div>
      <AnimatedDivider />
      <ContactSection content={c.contact} typo={typo.contact} />
      <Footer content={c.footer} typo={typo.footer} />
    </div>
  );
}

// ─── Admin Route ────────────────────────────────────────────
function AdminRoute() {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    async function check() {
      if (!token) { setChecking(false); return; }
      try {
        const valid = await verifyToken(token);
        if (valid) { setVerified(true); }
        else { localStorage.removeItem('admin_token'); setToken(''); }
      } catch {
        localStorage.removeItem('admin_token'); setToken('');
      } finally { setChecking(false); }
    }
    check();
  }, [token]);

  const handleLogin = (newToken) => { setToken(newToken); setVerified(true); };
  const handleLogout = () => { localStorage.removeItem('admin_token'); setToken(''); setVerified(false); };

  if (checking) return <div className="admin-loading" data-testid="admin-loading">Verifying session...</div>;
  if (!token || !verified) return <AdminLogin onLogin={handleLogin} />;
  return <AdminPanel token={token} onLogout={handleLogout} />;
}

// ─── Main App ───────────────────────────────────────────────
function App() {
  return (
    <div className="App">
      <Routes>
        <Route path="/admin" element={<AdminRoute />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
    </div>
  );
}

export default App;
